import type { MenuProps, SelectChangeEvent, SelectProps } from "@mui/material"
import { FormControl, Select } from "@mui/material"
import { memo, useCallback } from "react"
import { twMerge } from "tailwind-merge"

import { multiSelectListboxVariants, multiSelectPopupVariants } from "./multiSelect.variants"

import { type AerosTagData, AerosTagStack } from "@/components/UiKit"
import { SELECT_STYLING_PROPS } from "@/constants/forms"
import type { SelectOption } from "@/types/form-fields"

export interface MultiSelectProps {
  id?: string
  name?: string
  value?: string[]
  onChange?: (value: string[]) => void
  options: SelectOption[]
  error?: boolean
  disabled?: boolean
  children: React.ReactNode
  width?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "full"
  variant?: "default" | "inline"
  success?: boolean
  className?: string
  MenuProps?: Partial<MenuProps>
  placeholder?: string
  onScrollCapture?: (event: React.UIEvent<HTMLDivElement>) => void
}

/**
 * @deprecated This component is deprecated. Use `AerosMultiSelect` from `@/components/UiKit/AerosMultiSelect` instead.
 * For React Hook Form integration, use `FormMultiSelect` from `@/components/UiKit/FormControls/FormMultiSelect`.
 */
const MultiSelect = ({
  id,
  value = [],
  onChange,
  options,
  name,
  error,
  disabled,
  children,
  width = "md",
  placeholder = "Select At Least One",
  className,
  MenuProps: customMenuProps,
  onScrollCapture,
}: MultiSelectProps) => {
  const handleChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const selectedValues = event.target.value as string[]
      onChange?.(selectedValues)
    },
    [onChange]
  )

  const handleDelete = useCallback(
    (tagToDelete: AerosTagData) => {
      const newValue = value.filter((val) => val !== tagToDelete.value)
      onChange?.(newValue)
    },
    [value, onChange]
  )

  const tags: AerosTagData[] = value.map((val) => {
    const option = options.find((opt) => opt.value === val)
    return {
      key: val,
      label: option?.label ?? val,
      value: val,
    }
  })

  const defaultMenuProps = {
    PaperProps: {
      className: multiSelectPopupVariants({ width }),
    },
    MenuListProps: {
      className: multiSelectListboxVariants(),
    },
  }

  return (
    <FormControl fullWidth error={error}>
      <Select
        {...(SELECT_STYLING_PROPS as Partial<SelectProps<string[]>>)}
        id={id}
        name={name}
        multiple
        value={value}
        onChange={handleChange}
        disabled={disabled}
        onScrollCapture={onScrollCapture}
        renderValue={() => {
          if (!tags.length) {
            return <span className='text-gray-500'>{placeholder}</span>
          }
          return <AerosTagStack tags={tags} onDelete={handleDelete} disabled={disabled} />
        }}
        className={className}
        MenuProps={{
          ...SELECT_STYLING_PROPS.MenuProps,
          ...defaultMenuProps,
          ...customMenuProps,
          PaperProps: {
            ...defaultMenuProps.PaperProps,
            ...customMenuProps?.PaperProps,
            className: twMerge(defaultMenuProps.PaperProps?.className, customMenuProps?.PaperProps?.className),
          },
          MenuListProps: {
            ...defaultMenuProps.MenuListProps,
            ...customMenuProps?.MenuListProps,
            className: twMerge(defaultMenuProps.MenuListProps?.className, customMenuProps?.MenuListProps?.className),
          },
        }}
      >
        {children}
      </Select>
    </FormControl>
  )
}

MultiSelect.displayName = "MultiSelect"

export default memo(MultiSelect)
